// BatchCreate（CopyDrawer "批量新建" 模式）：表单驱动的模板批量新建。
//
// 用户在表单里直接用 `{n}` / `{n_cn}` / `{n_pad2}` / `{n+K}` / `{K+n}`
// 等占位符填模板，配合一个数字序列，提交时为每个 n 渲染一份 MapUpsertInput。
// 所有 JSON 字段（match / enter / edges / fight）内的 string 叶子都会被递归渲染。
//
// 注：enterConditions 当前不支持占位符——它是结构化对象（等级阈值/货币 ID 等
// 都是 number），常见场景里不需要按 n 变化。如果某天有需求，可以让 condition 的
// value/amount 也支持模板，但这会改 schema。
//
// 与 MapEditor 的区别：
// - 所有字符串字段把 placeholder 视为合法（不做 map_id 唯一性等强校验，由
//   BatchCreate 自己做"展开后唯一 + 与已有图冲突"校验）。
// - JSON 字段允许空字符串（fallback 到默认结构 [] / {}）。

import { useMemo, useState } from "react";
import type {
  GameContinent,
  GameMapSummary,
  MapUpsertInput,
} from "@/api/games";
import { Button } from "@/components/ui/Button";
import { Field, Input, Textarea } from "@/components/ui/Input";
import { StatusBadge } from "@/components/ui/StatusBadge";
import {
  deepRenderTemplates,
  parseSequenceInput,
  renderTemplate,
  type RenderContext,
} from "./batch-create";

export interface BatchCreateProps {
  maps: GameMapSummary[];
  continents: GameContinent[];
  submitting?: boolean;
  onSubmit: (payload: BatchCreateSubmitPayload) => Promise<void> | void;
  onCancel: () => void;
}

export interface BatchCreateSubmitPayload {
  upserts: MapUpsertInput[];
  conflictMapIDs: string[];
  overwriteConflicts: boolean;
}

interface JSONFieldState {
  text: string;
  error: string | null;
}

function emptyJSONField(fallback: string): JSONFieldState {
  return { text: fallback, error: null };
}

function validateJSON(text: string): string | null {
  const t = text.trim();
  if (!t) return null;
  try {
    JSON.parse(t);
    return null;
  } catch (e) {
    return (e as Error).message;
  }
}

function parseJSONOr<T>(text: string, fallback: T): T {
  const t = text.trim();
  if (!t) return fallback;
  try {
    return JSON.parse(t) as T;
  } catch {
    return fallback;
  }
}

interface PreviewRow {
  n: number;
  index: number;
  map_id: string;
  name: string;
  nested_group: string;
}

export function BatchCreate({
  maps,
  continents,
  submitting,
  onSubmit,
  onCancel,
}: BatchCreateProps) {
  const [continentID, setContinentID] = useState<string>(
    continents[0]?.continent_id ?? "",
  );
  const [mapIDTemplate, setMapIDTemplate] = useState("");
  const [nameTemplate, setNameTemplate] = useState("");
  const [nestedGroupTemplate, setNestedGroupTemplate] = useState("");
  const [sequenceRaw, setSequenceRaw] = useState("");
  const [selectableDefault, setSelectableDefault] = useState(true);
  const [allowTransit, setAllowTransit] = useState(false);

  const [matchJSON, setMatchJSON] = useState<JSONFieldState>(
    emptyJSONField('["{n}级地图"]'),
  );
  const [enterJSON, setEnterJSON] = useState<JSONFieldState>(emptyJSONField("[]"));
  const [edgesJSON, setEdgesJSON] = useState<JSONFieldState>(emptyJSONField("[]"));
  const [fightJSON, setFightJSON] = useState<JSONFieldState>(emptyJSONField("{}"));

  const [overwriteConflicts, setOverwriteConflicts] = useState(false);

  const sequence = useMemo(() => parseSequenceInput(sequenceRaw), [sequenceRaw]);
  const sequenceError = useMemo(() => {
    if (sequenceRaw.trim() === "") return null;
    if (sequence.length === 0) return "序列解析失败（用 2-9 或 2,3,5）";
    return null;
  }, [sequenceRaw, sequence]);

  const previewRows = useMemo<PreviewRow[]>(() => {
    if (sequence.length === 0 || !mapIDTemplate || !nameTemplate) return [];
    return sequence.map((n, i) => {
      const ctx: RenderContext = { n, index: i + 1 };
      return {
        n,
        index: ctx.index,
        map_id: renderTemplate(mapIDTemplate, ctx),
        name: renderTemplate(nameTemplate, ctx),
        nested_group: nestedGroupTemplate
          ? renderTemplate(nestedGroupTemplate, ctx)
          : "",
      };
    });
  }, [sequence, mapIDTemplate, nameTemplate, nestedGroupTemplate]);

  const existingMapIDs = useMemo(
    () => new Set(maps.map((m) => m.map_id)),
    [maps],
  );
  const conflictRows = useMemo(
    () => previewRows.filter((row) => existingMapIDs.has(row.map_id)),
    [previewRows, existingMapIDs],
  );

  const duplicatedInPreview = useMemo(() => {
    const seen = new Set<string>();
    const dup = new Set<string>();
    for (const r of previewRows) {
      if (seen.has(r.map_id)) dup.add(r.map_id);
      seen.add(r.map_id);
    }
    return dup;
  }, [previewRows]);

  const anyJSONError =
    !!matchJSON.error ||
    !!enterJSON.error ||
    !!edgesJSON.error ||
    !!fightJSON.error;

  const validationError = useMemo(() => {
    if (!continentID) return "请选择大陆";
    if (sequence.length === 0) return sequenceError ?? "请填序列";
    if (!mapIDTemplate) return "请填 map_id 模板";
    if (!nameTemplate) return "请填 name 模板";
    if (anyJSONError) return "有 JSON 字段格式非法";
    if (duplicatedInPreview.size > 0) {
      return `模板生成的 map_id 有重复：${Array.from(duplicatedInPreview).join(", ")}`;
    }
    for (const r of previewRows) {
      if (!/^[a-z0-9_]+$/.test(r.map_id)) {
        return `非法 map_id "${r.map_id}"（仅允许小写字母/数字/下划线）`;
      }
    }
    return null;
  }, [
    continentID,
    sequence,
    sequenceError,
    mapIDTemplate,
    nameTemplate,
    anyJSONError,
    duplicatedInPreview,
    previewRows,
  ]);

  const nonConflictCount = previewRows.length - conflictRows.length;
  const canSubmit =
    !submitting &&
    !validationError &&
    (overwriteConflicts ? previewRows.length > 0 : nonConflictCount > 0);

  function buildUpserts(): MapUpsertInput[] {
    const matchTpl = parseJSONOr<unknown>(matchJSON.text, []);
    const enterTpl = parseJSONOr<unknown>(enterJSON.text, []);
    const edgesTpl = parseJSONOr<unknown>(edgesJSON.text, []);
    const fightTpl = parseJSONOr<unknown>(fightJSON.text, {});
    const conflictSet = new Set(conflictRows.map((r) => r.map_id));

    const out: MapUpsertInput[] = [];
    sequence.forEach((n, i) => {
      const ctx: RenderContext = { n, index: i + 1 };
      const map_id = renderTemplate(mapIDTemplate, ctx);
      const isConflict = conflictSet.has(map_id);
      if (isConflict && !overwriteConflicts) return;

      const upsert: MapUpsertInput = {
        map_id,
        name: renderTemplate(nameTemplate, ctx),
        continent_id: continentID,
        nested_group: nestedGroupTemplate
          ? renderTemplate(nestedGroupTemplate, ctx)
          : "",
        selectable_default: selectableDefault,
        allow_transit_when_cooldown: allowTransit,
        match: deepRenderTemplates(matchTpl, ctx),
        enter: deepRenderTemplates(enterTpl, ctx),
        edges: deepRenderTemplates(edgesTpl, ctx),
        fight: deepRenderTemplates(fightTpl, ctx),
        enterConditions: [],
      };

      if (isConflict) {
        const existing = maps.find((m) => m.map_id === map_id);
        if (existing) {
          upsert.id = existing.id;
          upsert.version = existing.version;
        }
      }
      out.push(upsert);
    });
    return out;
  }

  async function handleSubmit() {
    if (!canSubmit) return;
    const upserts = buildUpserts();
    await onSubmit({
      upserts,
      conflictMapIDs: conflictRows.map((r) => r.map_id),
      overwriteConflicts,
    });
  }

  return (
    <div className="flex flex-1 flex-col gap-4 overflow-y-auto pr-1">
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        <Field label="大陆">
          <select
            value={continentID}
            onChange={(e) => setContinentID(e.target.value)}
            className="h-8 w-full rounded border border-[hsl(var(--input))] bg-transparent px-2 text-sm"
          >
            {continents.length === 0 ? <option value="">（无大陆）</option> : null}
            {continents.map((c) => (
              <option key={c.continent_id} value={c.continent_id}>
                {c.name || c.continent_id}
              </option>
            ))}
          </select>
        </Field>
        <Field
          label="序列"
          hint='范围 "2-9" 或列表 "2,3,5"'
          error={sequenceError ?? undefined}
        >
          <Input
            value={sequenceRaw}
            onChange={(e) => setSequenceRaw(e.target.value)}
            placeholder="2-9"
          />
        </Field>
        <Field label="map_id 模板" hint="例：wuhun_floor_{n}">
          <Input
            value={mapIDTemplate}
            onChange={(e) => setMapIDTemplate(e.target.value)}
            placeholder="wuhun_floor_{n}"
          />
        </Field>
        <Field label="name 模板" hint="例：武魂塔{n_cn}层">
          <Input
            value={nameTemplate}
            onChange={(e) => setNameTemplate(e.target.value)}
            placeholder="武魂塔{n_cn}层"
          />
        </Field>
        <Field
          label="nested_group 模板"
          hint="留空=不分组；填模板=每张图独立链"
        >
          <Input
            value={nestedGroupTemplate}
            onChange={(e) => setNestedGroupTemplate(e.target.value)}
            placeholder="wuhun_floor_{n}_nested"
          />
        </Field>
        <div className="flex items-end gap-4">
          <label className="flex items-center gap-2 text-xs">
            <input
              type="checkbox"
              checked={selectableDefault}
              onChange={(e) => setSelectableDefault(e.target.checked)}
            />
            默认可挂
          </label>
          <label className="flex items-center gap-2 text-xs">
            <input
              type="checkbox"
              checked={allowTransit}
              onChange={(e) => setAllowTransit(e.target.checked)}
            />
            冷却期允许过路
          </label>
        </div>
      </div>

      <div className="text-xs text-[hsl(var(--muted-foreground))]">
        JSON 字段内的字符串值都会按当前 n 渲染占位符（例 enter[].data.Act =
        <code className="mx-1 rounded bg-[hsl(var(--muted))] px-1">@武魂塔{"{n}"}</code>，
        edges[].to =<code className="mx-1 rounded bg-[hsl(var(--muted))] px-1">wuhun_mirror_{"{n}"}</code>，
        data.index =<code className="mx-1 rounded bg-[hsl(var(--muted))] px-1">{"{138+n}"}</code>）。
      </div>

      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        <JSONField
          label="match JSON"
          state={matchJSON}
          onChange={(text) => setMatchJSON({ text, error: validateJSON(text) })}
        />
        <JSONField
          label="enter JSON"
          state={enterJSON}
          onChange={(text) => setEnterJSON({ text, error: validateJSON(text) })}
        />
        <JSONField
          label="edges JSON"
          state={edgesJSON}
          onChange={(text) => setEdgesJSON({ text, error: validateJSON(text) })}
        />
        <JSONField
          label="fight JSON"
          state={fightJSON}
          onChange={(text) => setFightJSON({ text, error: validateJSON(text) })}
        />
      </div>

      {previewRows.length > 0 ? (
        <div className="rounded border border-[hsl(var(--border))] p-2 text-xs">
          <div className="mb-1 flex items-center justify-between">
            <div className="font-semibold">
              将创建 {previewRows.length} 张地图
            </div>
            {conflictRows.length > 0 ? (
              <label className="flex items-center gap-1.5">
                <input
                  type="checkbox"
                  checked={overwriteConflicts}
                  onChange={(e) => setOverwriteConflicts(e.target.checked)}
                />
                覆盖现有 {conflictRows.length} 张
              </label>
            ) : null}
          </div>
          <ul className="max-h-40 space-y-0.5 overflow-y-auto">
            {previewRows.map((row) => {
              const conflict = existingMapIDs.has(row.map_id);
              return (
                <li
                  key={`${row.index}-${row.map_id}`}
                  className="flex items-center gap-2"
                >
                  <span className="font-mono">{row.map_id}</span>
                  <span className="truncate">{row.name}</span>
                  {row.nested_group ? (
                    <span className="text-[hsl(var(--muted-foreground))]">
                      [{row.nested_group}]
                    </span>
                  ) : null}
                  {conflict ? (
                    <StatusBadge variant="warn">已存在</StatusBadge>
                  ) : null}
                </li>
              );
            })}
          </ul>
        </div>
      ) : null}

      {validationError ? (
        <div className="text-xs text-[hsl(var(--destructive))]">
          {validationError}
        </div>
      ) : null}

      <div className="mt-auto flex items-center justify-end gap-2 pt-3">
        <Button type="button" variant="outline" size="sm" onClick={onCancel}>
          取消
        </Button>
        <Button
          type="button"
          size="sm"
          onClick={handleSubmit}
          disabled={!canSubmit}
        >
          {submitting
            ? "创建中…"
            : conflictRows.length > 0 && overwriteConflicts
              ? `创建并覆盖（${previewRows.length} 张）`
              : `创建（${previewRows.length - conflictRows.length} 张${
                  conflictRows.length > 0
                    ? `，跳过 ${conflictRows.length}`
                    : ""
                }）`}
        </Button>
      </div>
    </div>
  );
}

function JSONField({
  label,
  state,
  onChange,
}: {
  label: string;
  state: JSONFieldState;
  onChange: (text: string) => void;
}) {
  return (
    <Field label={label} error={state.error ?? undefined}>
      <Textarea
        rows={5}
        value={state.text}
        onChange={(e) => onChange(e.target.value)}
        spellCheck={false}
      />
    </Field>
  );
}
